// components/UserList.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';

const UserList = () => {
    const [users, setUsers] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [perPage, setPerPage] = useState(10);
    const [error, setError] = useState(null);
    const apiUrl = process.env.REACT_APP_API_BASE_URL;

    useEffect(() => {
        fetchUsers();
    }, [currentPage, perPage]);

    const fetchUsers = async () => {
        setLoading(true);
        setError(null);
        
        try {
            const response = await axios.get(`${apiUrl}/api/posperpage`, {
                params: {
                    page: currentPage,
                    per_page: perPage
                }
            });
            
            const data = response.data;
            
            setUsers(data.data);
            setCurrentPage(data.current_page);
            setLastPage(data.last_page);
        } catch (error) {
            console.error('Error fetching users:', error);
            setError(error.response?.data?.message || 'Failed to fetch data');
        } finally {
            setLoading(false);
        }
    };

    const goToNextPage = () => {
        if (currentPage < lastPage) {
            setCurrentPage(currentPage + 1);
        }
    };

    const goToPreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const goToPage = (page) => {
        setCurrentPage(page);
    };

    const handlePerPageChange = (e) => {
        setPerPage(Number(e.target.value));
        setCurrentPage(1);
    };

    const handleRetry = () => {
        fetchUsers();
    };

    if (loading) {
        return <div className="loading">Loading...</div>;
    }

    if (error) {
        return (
            <div className="error-container">
                <div className="error-message">Error: {error}</div>
                <button onClick={handleRetry} className="retry-button">
                    Retry
                </button>
            </div>
        );
    }

    return (
        <div className="user-list-container">
            <h2>Users List</h2>
            
            {/* Per Page Selector */}
            <div className="per-page-selector">
                <label>Items per page: </label>
                <select value={perPage} onChange={handlePerPageChange}>
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                </select>
            </div>

            {/* Users Table */}
            <div className="table-responsive">
                <table className="users-table">
                    <thead>
                        <tr>
                            <th>PO Number</th>
                            <th>Status</th>
                            <th>Mode</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.length > 0 ? (
                            users.map((user, index) => (
                                <tr key={user.id || index}>
                                    <td>{user.po_no}</td>
                                    <td>
                                        <span className={`status-badge status-${user.status?.toLowerCase()}`}>
                                            {user.status}
                                        </span>
                                    </td>
                                    <td>{user.mode}</td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="3" className="no-data">
                                    No data available
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination Controls */}
            {lastPage > 1 && (
                <>
                    <div className="pagination">
                        <button 
                            onClick={goToPreviousPage} 
                            disabled={currentPage === 1}
                            className="pagination-button"
                        >
                            Previous
                        </button>
                        
                        {/* First page */}
                        {currentPage > 3 && (
                            <>
                                <button onClick={() => goToPage(1)} className="pagination-number">
                                    1
                                </button>
                                {currentPage > 4 && <span className="pagination-dots">...</span>}
                            </>
                        )}
                        
                        {/* Page numbers around current page */}
                        {[...Array(lastPage)].map((_, index) => {
                            const pageNum = index + 1;
                            // Show current page and 2 pages before/after
                            if (
                                pageNum === 1 ||
                                pageNum === lastPage ||
                                (pageNum >= currentPage - 2 && pageNum <= currentPage + 2)
                            ) {
                                return (
                                    <button
                                        key={pageNum}
                                        onClick={() => goToPage(pageNum)}
                                        className={`pagination-number ${currentPage === pageNum ? 'active' : ''}`}
                                    >
                                        {pageNum}
                                    </button>
                                );
                            }
                            return null;
                        })}
                        
                        {/* Last page indicator */}
                        {currentPage < lastPage - 2 && (
                            <>
                                {currentPage < lastPage - 3 && <span className="pagination-dots">...</span>}
                                <button onClick={() => goToPage(lastPage)} className="pagination-number">
                                    {lastPage}
                                </button>
                            </>
                        )}
                        
                        <button 
                            onClick={goToNextPage} 
                            disabled={currentPage === lastPage}
                            className="pagination-button"
                        >
                            Next
                        </button>
                    </div>

                    <div className="page-info">
                        Page {currentPage} of {lastPage}
                    </div>
                </>
            )}
        </div>
    );
};

export default UserList;